import {
  generateCodeChallenge,
  generateCodeVerifier,
  generateState,
  storePkceSession,
} from './pkce/pkce'

export interface AzureSsoDiscovery {
  tenantId: string
  clientId: string
  accountId: string
  scope: string
  loginHint?: string
  // 'none' signs in silently against the existing Entra session, or redirects back with an error
  prompt?: 'none'
  returnPath?: string
}

export interface EntraTokenResponse {
  accessToken: string
  refreshToken?: string
  expiresIn?: number
  idToken?: string
}

function decodeJwtPayload(token?: string): any {
  try {
    const payload = token?.split('.')[1]
    if (!payload) return null
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(decodeURIComponent(escape(json)))
  } catch {
    return null
  }
}

// The Entra account to target on a silent sign-in, so the right one is picked when several are signed in
export function getEntraLoginHint(tokens: EntraTokenResponse): string | undefined {
  const claims = decodeJwtPayload(tokens.idToken) ?? decodeJwtPayload(tokens.accessToken)
  return claims?.login_hint || claims?.preferred_username || claims?.upn || claims?.email
}

function buildFullScope(scope: string): string {
  const parts = new Set(scope.split(' ').filter(Boolean))
  parts.add('openid')
  parts.add('offline_access')
  parts.add('profile')
  return Array.from(parts).join(' ')
}

export async function beginAzureSsoPkceLogin(discovery: AzureSsoDiscovery): Promise<void> {
  const {tenantId, clientId, accountId, scope, loginHint, prompt, returnPath} = discovery
  const redirectUri = `${window.location.origin}/sso-login/${accountId}`
  const fullScope = buildFullScope(scope)
  const verifier = generateCodeVerifier()
  const challenge = await generateCodeChallenge(verifier)
  const state = generateState()

  storePkceSession({
    verifier,
    state,
    accountId,
    tenantId,
    clientId,
    redirectUri,
    scope: fullScope,
    silent: prompt === 'none',
    returnPath,
  })

  const params = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    redirect_uri: redirectUri,
    response_mode: 'query',
    scope: fullScope,
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  })

  if (loginHint) {
    params.set('login_hint', loginHint)
  }

  if (prompt) {
    params.set('prompt', prompt)
  }

  const url = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?${params.toString()}`
  window.location.assign(url)
}

export async function exchangeAzureAuthorizationCode(params: {
  tenantId: string
  clientId: string
  code: string
  redirectUri: string
  codeVerifier: string
  scope: string
}): Promise<EntraTokenResponse> {
  const body = new URLSearchParams({
    client_id: params.clientId,
    grant_type: 'authorization_code',
    code: params.code,
    redirect_uri: params.redirectUri,
    code_verifier: params.codeVerifier,
    scope: params.scope,
  })

  const response = await fetch(
    `https://login.microsoftonline.com/${params.tenantId}/oauth2/v2.0/token`,
    {
      method: 'POST',
      headers: {'Content-Type': 'application/x-www-form-urlencoded'},
      body,
    }
  )

  const data = await response.json()
  if (!response.ok || !data.access_token) {
    const message = data.error_description || data.error || 'Azure sign-in failed'
    throw new Error(message)
  }

  return {
    accessToken: data.access_token as string,
    refreshToken: data.refresh_token as string | undefined,
    expiresIn: data.expires_in as number | undefined,
    idToken: data.id_token as string | undefined,
  }
}

export async function refreshEntraAccessToken(params: {
  tenantId: string
  clientId: string
  refreshToken: string
  scope: string
}): Promise<EntraTokenResponse> {
  const body = new URLSearchParams({
    client_id: params.clientId,
    grant_type: 'refresh_token',
    refresh_token: params.refreshToken,
    scope: params.scope,
  })

  const response = await fetch(
    `https://login.microsoftonline.com/${params.tenantId}/oauth2/v2.0/token`,
    {
      method: 'POST',
      headers: {'Content-Type': 'application/x-www-form-urlencoded'},
      body,
    }
  )

  const data = await response.json()
  if (!response.ok || !data.access_token) {
    const message = data.error_description || data.error || 'Azure token refresh failed'
    throw new Error(message)
  }

  return {
    accessToken: data.access_token as string,
    refreshToken: data.refresh_token as string | undefined,
    expiresIn: data.expires_in as number | undefined,
    idToken: data.id_token as string | undefined,
  }
}
